<template>
	<div class="container pt-6 pb-10">
		<div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 py-2 mb-4">
			<h1 class="page-title">Lucrari</h1>
			<div class="flex flex-row flex-wrap justify-end gap-2 sm:ml-auto">
				<v-btn v-for="cal in calendars" :key="cal.type" :to="'/lucrari/calendar/' + cal.type" color="blue-grey"
					class="uppercase" dark>
					<v-icon left>{{ cal.icon }}</v-icon>
					{{ cal.title }}
				</v-btn>
			</div>
		</div>

		<div class="light-grey rounded mb-4 p-4">
			<div class="flex flex-col lg:flex-row lg:items-center gap-2">
				<v-text-field v-model="search" class="w-full lg:w-1/3" clearable flat solo hide-details
					prepend-inner-icon="mdi-magnify" label="Cauta dupa client"></v-text-field>
				<v-select v-model="filterStages" class="w-full lg:w-1/3" :items="openStages" multiple
					clearable chips small-chips deletable-chips label="Stadiu" solo hide-details></v-select>
				<v-select v-if="!isAgent || isExportAgent(user)" v-model="filterAgent" class="w-full lg:w-48" :items="agentOptions"
					clearable label="Agent" solo hide-details></v-select>
				<v-select v-model="filterDetalii" class="w-full lg:w-48" :items="detaliiOptions" clearable
					label="Detalii" solo hide-details></v-select>
				<v-select v-model="filterVopsire" class="w-full lg:w-48" :items="vopsireOptions" clearable
					label="Vopsire" solo hide-details></v-select>
				<v-select v-model="filterInterventie" class="w-full lg:w-48" :items="interventieOptions" clearable
					label="Interventie" solo hide-details></v-select>
				<!-- postponed works are left out unless asked for -->
				<v-select v-model="filterAmanare" class="w-full lg:w-48" :items="amanareOptions" clearable
					label="Fara amanate" solo hide-details></v-select>
				<!-- an export agent sees only EU works: shown, fixed -->
				<v-select v-model="filterCountry" class="w-full lg:w-32" :items="countries" label="Tara" solo
					hide-details :disabled="isExportAgent(user)" @change="load"></v-select>
				<v-switch v-model="onlyActive" class="mt-0 lg:ml-2" label="Doar active" hide-details inset
					@change="load"></v-switch>
			</div>
		</div>

		<v-sheet class="p-4 rounded mb-1 flex flex-row flex-wrap gap-2">
			<v-chip v-for="s in stageCounts" :key="s.value" :color="s.color" :outlined="!filterStages.includes(s.value)"
				:dark="filterStages.includes(s.value)" label @click="toggleStage(s.value)">
				{{ s.text }}
				<strong class="ml-2">{{ s.count }}</strong>
			</v-chip>
			<!-- shortcuts for the Interventie and Amanare filters, beside the stages -->
			<v-chip :color="interventie.color" :outlined="filterInterventie !== '1'" :dark="filterInterventie === '1'"
				label @click="filterInterventie = filterInterventie === '1' ? null : '1'">
				<v-icon left small>mdi-tools</v-icon>
				Interventii
				<strong class="ml-2">{{ interventiiCount }}</strong>
			</v-chip>
			<v-chip :color="amanata.color" :outlined="filterAmanare !== '1'" :dark="filterAmanare === '1'"
				label @click="filterAmanare = filterAmanare === '1' ? null : '1'">
				<v-icon left small>mdi-pause-circle</v-icon>
				Amanate
				<strong class="ml-2">{{ amanateCount }}</strong>
			</v-chip>
		</v-sheet>

		<v-data-table :headers="headers" :items="filteredWorks" item-key="id_work" :loading="loadingWorks"
			:items-per-page="50" :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }" class="cursor-pointer"
			@click:row="(item) => $router.push('/lucrari/' + item.id_work)">
			<template v-slot:item.client_nume="{ item }">
				<div class="font-bold">{{ item.client_nume }}</div>
				<div class="text-sm text-gray-600">{{ location(item) }}</div>
			</template>
			<template v-slot:item.data_contract="{ item }">
				{{ item.data_contract | formatDate }}
			</template>
			<template v-slot:item.stage="{ item }">
				<div class="flex flex-row flex-wrap items-center gap-1">
					<WorkStageChip :stage="item.stage" small />
					<!-- second status: a finished work with open interventions -->
					<v-chip v-if="isAmanata(item)" :color="amanata.color" small dark label :title="item.motiv_amanare">
						{{ amanata.text }}</v-chip>
					<v-chip v-if="inInterventie(item)" :color="interventie.color" small dark label
						:title="item.open_interventii + ' interventii deschise'">{{ interventie.text }}</v-chip>
				</div>
			</template>
			<!-- the work's markers, information only (set on the work's page): details, painting,
			     open observations, open interventions -->
			<template v-slot:item.statusuri="{ item }">
				<div class="flex flex-row items-center gap-3 whitespace-nowrap">
					<v-icon :color="detaliiInfo(item).color" :title="detaliiInfo(item).text">
						{{ detaliiInfo(item).icon }}
					</v-icon>
					<!-- the extra elements sent to be painted; the whole work shows in the colour cell -->
					<v-icon v-if="vopsireColor(item)" :color="vopsireColor(item)" :title="elementeTitle(item)">mdi-spray</v-icon>
					<v-icon v-else color="grey lighten-1" title="Fara elemente de vopsit">mdi-spray-bottle</v-icon>
					<v-badge :value="Number(item.open_observatii) > 0" :content="item.open_observatii" color="orange"
						overlap bordered>
						<v-icon :color="Number(item.open_observatii) > 0 ? 'orange' : 'grey lighten-1'"
							:title="Number(item.open_observatii) > 0 ? item.open_observatii + ' observatii deschise' : 'Nicio observatie deschisa'">
							mdi-note-text-outline
						</v-icon>
					</v-badge>
					<v-badge :value="Number(item.open_interventii) > 0" :content="item.open_interventii" color="red"
						overlap bordered>
						<v-icon :color="Number(item.open_interventii) > 0 ? 'red' : 'grey lighten-1'"
							:title="Number(item.open_interventii) > 0 ? item.open_interventii + ' interventii deschise' : 'Nicio interventie deschisa'">
							mdi-tools
						</v-icon>
					</v-badge>
				</div>
			</template>
			<template v-slot:item.color="{ item }">
				<!-- a layer filling the cell, the colour on top: yellow while the colour is to order or the
				     whole work to send to the painter, blue while it is at the painter, green once back -->
				<div v-if="colorCell(item)" class="cell-bg" :class="'cell-bg--' + colorCell(item).state"
					:title="colorCell(item).title"></div>
				<RalSwatch :color="item.color" class="relative" :title="colorCell(item) ? colorCell(item).title : ''" />
			</template>
			<!-- the fence models, or the slat widths of older models; all slat widths on hover -->
			<template v-slot:item.models="{ item }">
				<span v-if="fenceModels(item).length" :title="slatPatternsTitle(item.slat_patterns)">
					{{ fenceModels(item).join(", ") }}</span>
				<span v-else class="text-gray-400">-</span>
				<span v-if="fenceArea(item) > 0" class="whitespace-nowrap text-gray-600 ml-1">
					&middot; {{ fenceArea(item).toFixed(2) }} m²</span>
			</template>
			<!-- the three bookings, one per line: label and date, or - when not booked yet -->
			<template v-slot:item.programari="{ item }">
				<div class="programari-cell">
					<span class="text-gray-600">Masuratori</span>
					<span>{{ item.masuratori_start ? $options.filters.formatDate(item.masuratori_start) : "-" }}</span>
					<span class="text-gray-600">Productie</span>
					<span>{{ item.productie_start ? range(item.productie_start, item.productie_end) : "-" }}</span>
					<span class="text-gray-600">Montaj</span>
					<span>{{ item.montaj_start ? range(item.montaj_start, item.montaj_end) : "-" }}</span>
				</div>
			</template>
			<template v-slot:item.data_montaj_maxima="{ item }">
				<div v-if="item.data_montaj_maxima" class="flex flex-col">
					<span>{{ item.data_montaj_maxima | formatDate }}</span>
					<span v-if="!isClosed(item.stage)" class="text-sm font-bold"
						:class="deadlineClass(item)">{{ deadlineText(item) }}</span>
				</div>
				<span v-else class="text-gray-400">-</span>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import {
	STAGES, CALENDARS, daysToDeadline, deadlineColor, detaliiInfo, fenceModels, fenceArea, slatPatternsTitle, isClosed, INTERVENTIE, inInterventie, isWorksAgent, isExportAgent, agentFilters, montageLocation, hasSpecialColor, hasVopsire, vopsirePartState, AMANATA, isAmanata,
} from "@/utils/works";
import WorkStageChip from "./WorkStageChip";
import RalSwatch from "./RalSwatch";

export default {
	name: "WorksList",
	components: { WorkStageChip, RalSwatch },
	data() {
		// filters picked earlier in this session (the store keeps them across screens)
		const saved = this.$store.state.works.listFilters || {};
		// EU works first for whoever works the export market (Users.agent_market 'export'), whatever
		// their role - a superuser too; an export agent sees nothing else (fixed below)
		const user = this.$store.getters.loggedUser;
		const euFirst = !!user && user.agent_market === "export";
		return {
			search: "",
			filterStages: [],
			filterAgent: null,
			filterCountry: euFirst ? "eu" : "ro",
			filterDetalii: null,
			filterVopsire: null,
			filterInterventie: null,
			filterAmanare: null,
			...saved,
			...(saved.filterStages ? { filterStages: [...saved.filterStages] } : {}),
			...(isExportAgent(user) ? { filterCountry: "eu" } : {}),
			interventieOptions: [
				{ text: "Cu interventii deschise", value: "1" },
				{ text: "Fara interventii deschise", value: "0" },
			],
			interventie: INTERVENTIE,
			amanata: AMANATA,
			amanareOptions: [
				{ text: "Doar amanate", value: "1" },
				{ text: "Inclusiv amanate", value: "toate" },
			],
			vopsireOptions: [
				{ text: "Cu vopsire", value: "1" },
				{ text: "Fara vopsire", value: "0" },
				{ text: "De trimis la vopsit", value: "de_trimis" },
				{ text: "La vopsit", value: "la_vopsit" },
				{ text: "Returnata de la vopsit", value: "returnata" },
			],
			detaliiOptions: [
				{ text: "Detalii stabilite", value: "1" },
				{ text: "Detalii nestabilite", value: "0" },
			],
			// off: every work, finished ones included; on: only the ones still in progress
			onlyActive: saved.onlyActive || false,
			countries: [
				{ text: "RO", value: "ro" },
				{ text: "EU", value: "eu" },
				{ text: "Toate", value: null },
			],
			headers: [
				{ text: "Contract", value: "contract_id", width: 90 },
				{ text: "Client", value: "client_nume" },
				{ text: "Agent", value: "agent" },
				{ text: "Data contract", value: "data_contract" },
				{ text: "Stadiu", value: "stage" },
				{ text: "Statusuri", value: "statusuri", sortable: false },
				// color-cell: the cell the yellow layer of a special colour is positioned in
				{ text: "Culoare", value: "color", sortable: false, cellClass: "color-cell" },
				{ text: "Model", value: "models", sortable: false },
				{ text: "Programari", value: "programari", sortable: false },
				{ text: "Montaj maxim", value: "data_montaj_maxima" },
			],
		};
	},
	computed: {
		...mapState("works", ["works", "loadingWorks"]),
		user() {
			return this.$store.getters.loggedUser;
		},
		listFilters() {
			return {
				search: this.search,
				filterStages: [...this.filterStages],
				filterAgent: this.filterAgent,
				filterCountry: this.filterCountry,
				filterDetalii: this.filterDetalii,
				filterVopsire: this.filterVopsire,
				filterInterventie: this.filterInterventie,
				filterAmanare: this.filterAmanare,
				onlyActive: this.onlyActive,
			};
		},
		isAgent() {
			return isWorksAgent(this.user);
		},
		// Everyone sees the three calendars; agents only their works, read-only past measurements.
		calendars() {
			return Object.values(CALENDARS);
		},
		openStages() {
			// active works include finished ones with open interventions: their stages show when there
			// are any
			return STAGES.filter(
				(s) => !this.onlyActive || !isClosed(s.value) || this.filterStages.includes(s.value) ||
					this.works.some((w) => w.stage === s.value)
			);
		},
		agentOptions() {
			const names = [...new Set(this.works.map((w) => w.agent).filter(Boolean))];
			return names.sort();
		},
		// Works matching search, agent, details and painting: what every count below starts from.
		baseWorks() {
			const term = (this.search || "").toLowerCase();
			return this.works.filter(
				(w) =>
					(!term || (w.client_nume || "").toLowerCase().includes(term)) &&
					(!this.filterAgent || w.agent === this.filterAgent) &&
					(this.filterDetalii === null || this.filterDetalii === undefined || String(w.detalii_stabilite) === this.filterDetalii) &&
					this.matchesVopsire(w)
			);
		},
		// ...and the interventie / postponed filters, before the stage filter, so the stage counts
		// stay useful.
		searchedWorks() {
			return this.baseWorks.filter((w) => this.matchesInterventie(w) && this.matchesAmanare(w));
		},
		// Each shortcut counts its works under the other filters, whatever its own is set to.
		interventiiCount() {
			return this.baseWorks.filter((w) => inInterventie(w) && this.matchesAmanare(w)).length;
		},
		amanateCount() {
			return this.baseWorks.filter((w) => isAmanata(w) && this.matchesInterventie(w)).length;
		},
		filteredWorks() {
			if (!this.filterStages.length) return this.searchedWorks;
			return this.searchedWorks.filter((w) => this.filterStages.includes(w.stage));
		},
		stageCounts() {
			return this.openStages.map((s) => ({
				...s,
				count: this.searchedWorks.filter((w) => w.stage === s.value).length,
			}));
		},
	},
	methods: {
		detaliiInfo,
		fenceModels,
		fenceArea,
		slatPatternsTitle,
		isClosed,
		inInterventie,
		isExportAgent,
		load() {
			const filters = {};
			if (this.filterCountry) filters.country = this.filterCountry;
			if (!this.onlyActive) filters.includeClosed = 1;
			if (this.isAgent) Object.assign(filters, agentFilters(this.user));
			this.$store.dispatch("works/fetchWorks", filters).catch(() => {});
		},
		toggleStage(stage) {
			const i = this.filterStages.indexOf(stage);
			if (i > -1) this.filterStages.splice(i, 1);
			else this.filterStages.push(stage);
		},
		hasSpecialColor,
		// The spray icon shows only the extra elements: amber to send, blue at the painter, green back
		vopsireColor(item) {
			return { de_trimis: "amber darken-3", la_vopsit: "blue", returnata: "green" }[vopsirePartState(item, "elemente")];
		},
		elementeTitle(item) {
			const text = { de_trimis: "de trimis", la_vopsit: "la vopsit", returnata: "returnate" }[vopsirePartState(item, "elemente")];
			return `Extra elemente ${text}: ${item.vopsire_elemente}`;
		},
		matchesInterventie(w) {
			return !this.filterInterventie || String(Number(inInterventie(w))) === this.filterInterventie;
		},
		// Postponed works only when asked for: none by default, only them, or all
		matchesAmanare(w) {
			if (this.filterAmanare === "toate") return true;
			return isAmanata(w) === (this.filterAmanare === "1");
		},
		isAmanata,
		// Cu / fara vopsire, or where the painting is: the whole work or the extra elements, either
		// one in that state (e.g. "La vopsit" finds the work at the painter while its elements are
		// still to send)
		matchesVopsire(w) {
			const f = this.filterVopsire;
			if (f === null || f === undefined) return true;
			if (f === "1" || f === "0") return String(hasVopsire(w) ? 1 : 0) === f;
			return ["lucrare", "elemente"].some((key) => vopsirePartState(w, key) === f);
		},
		// The colour cell's background: by the whole work's external painting when there is one
		// (the extra elements show on the spray icon), else yellow for a colour or slat to order
		// specially; null for a stock colour not sent to be painted.
		colorCell(item) {
			const state = vopsirePartState(item, "lucrare");
			const special = hasSpecialColor(item);
			if (state) {
				const text = { de_trimis: "de trimis la vopsit", la_vopsit: "la vopsit", returnata: "returnata de la vopsit" }[state];
				return { state, title: (special ? "Culoare speciala" : "Vopsire externa") + " - toata lucrarea " + text };
			}
			return special ? { state: "de_trimis", title: "Culoare sau lamela de comandat special" } : null;
		},
		hasVopsire,
		location(item) {
			return montageLocation(item);
		},
		// Event ends are exclusive (midnight of the next day); show the last day worked.
		range(start, end) {
			const s = moment(start);
			const e = moment(end).subtract(1, "minute");
			return s.isSame(e, "day") ? s.format("DD.MM") : s.format("DD.MM") + " - " + e.format("DD.MM");
		},
		deadlineText(item) {
			const days = daysToDeadline(item);
			if (days < 0) return `depasit cu ${-days} zile`;
			if (days === 0) return "astazi";
			return `${days} zile`;
		},
		deadlineClass(item) {
			return deadlineColor(daysToDeadline(item)) + "--text";
		},
	},
	watch: {
		// keep the filters in the store as they change
		listFilters: {
			handler(filters) {
				this.$store.commit("works/setListFilters", filters);
			},
			deep: true,
		},
	},
	created() {
		this.load();
	},
};
</script>

<style scoped>
/* A colour that has to be ordered specially (or 200 mm slats): a yellow layer covering the whole
   cell - all of its height, which the row's two-line cells set - with the colour drawn on top. */
.v-data-table ::v-deep td.color-cell {
	position: relative;
}
/* label | date, three lines */
.programari-cell {
	display: grid;
	grid-template-columns: auto auto;
	column-gap: 8px;
	padding: 4px 0;
	font-size: 0.875rem;
	line-height: 1.4;
	white-space: nowrap;
}
.cell-bg {
	position: absolute;
	inset: 0;
	background-color: #fff59d;
}
/* yellow by default (to order / to send), blue at the painter, green back */
.cell-bg--la_vopsit {
	background-color: #bbdefb;
}
.cell-bg--returnata {
	background-color: #c8e6c9;
}
</style>
